import { Alert, Button, Stack, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../api/client.js';
import { APP_LOCALE } from '../utils/locale.js';

interface Screening {
  screened: boolean;
  screeningId: string;
  status: string;
  riskScore?: number;
  checks: string[];
  checksFlagged: string[];
  findings: { rule: string; reason: string; score?: number; maxScore?: number }[];
  customerRiskLevel?: string;
  customerExternalId?: string;
  customerParty?: string;
  detail: string;
  reviewVersion: string;
  reviewRequired: boolean;
  clearedBy: string;
  clearedAt: string;
  clearanceReason: string;
  localRules?: {
    policyVersion: string;
    reviewRequired: boolean;
    findings: { ruleId: string; ruleName: string; outcome: string; reason: string }[];
  };
  valuation?: {
    eurAmountMinor: string;
    originalCurrency: string;
    currencyPerEur: string;
    source: string;
    rateDate: string;
    valuedAt: string;
  };
}

/** Recording clearance does not approve, debit or credit the payment. */
export function PaymentScreeningPanel({
  path,
  onReady,
}: {
  path: string;
  onReady: (ready: boolean) => void;
}): JSX.Element {
  const [result, setResult] = useState<Screening | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const load = useCallback(
    async (retry = false): Promise<void> => {
      setBusy(true);
      setError('');
      onReady(false);
      try {
        setResult(
          await apiFetch<Screening>(`${path}/screening`, { method: 'POST', body: { retry } }),
        );
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Screening could not be loaded.');
      } finally {
        setBusy(false);
      }
    },
    [path, onReady],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const ready =
    result !== null &&
    result.reviewVersion !== '' &&
    (!result.reviewRequired || (!!result.clearedBy && !!result.clearedAt));
  useEffect(() => {
    onReady(!busy && !error && ready);
  }, [busy, error, ready, onReady]);

  const clear = async (): Promise<void> => {
    if (result === null) return;
    setBusy(true);
    setError('');
    onReady(false);
    try {
      setResult(
        await apiFetch<Screening>(`${path}/screening/clear`, {
          method: 'POST',
          body: { version: result.reviewVersion, reason: reason.trim() },
        }),
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Clearance could not be recorded.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Stack spacing={1.5} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
      <Typography variant="subtitle2">Transaction screening</Typography>
      {result === null ? (
        <Typography variant="body2">
          {busy ? 'Checking screening…' : 'Screening has not loaded.'}
        </Typography>
      ) : (
        <>
          <Alert severity={ready ? 'success' : 'warning'}>
            {ready
              ? result.clearedBy
                ? 'Compliance clearance recorded. You can now approve the payment.'
                : 'Screening completed with no findings.'
              : result.reviewVersion
                ? 'Compliance clearance is required before approval.'
                : 'Screening is pending or required information is missing.'}
          </Alert>
          <Typography variant="body2">
            {result.screened
              ? `Provider status: ${result.status}.`
              : 'Provider screening is unavailable or incomplete.'}
          </Typography>
          {result.detail && <Typography variant="body2">{result.detail}</Typography>}
          {result.customerRiskLevel && (
            <Stack spacing={0.5}>
              <Typography variant="body2">
                Customer risk submitted as {result.customerParty === 'REMITTER' ? 'sender' : 'beneficiary'}: {result.customerRiskLevel.replace('_RISK', '').toLowerCase()}.
              </Typography>
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                {result.customerExternalId ? `Linked provider profile: ${result.customerExternalId}` : 'No provider profile was linked to this screening.'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This is the risk used for this screening. The provider profile’s stored risk has not been verified.
              </Typography>
            </Stack>
          )}
          {result.valuation && (
            <Typography variant="body2">
              Monitoring value: EUR {formatEurMinor(result.valuation.eurAmountMinor)}.
              {result.valuation.source === 'ECB'
                ? ` ECB reference dated ${result.valuation.rateDate}: 1 EUR = ${result.valuation.currencyPerEur} ${result.valuation.originalCurrency}. Rounded to cents using half-even.`
                : ' Original payment is in EUR.'}
              {' Saved at '}
              {new Date(result.valuation.valuedAt).toLocaleString(APP_LOCALE)}
              {'. The payment stays in its original currency.'}
            </Typography>
          )}
          {result.screened && (
            /* The provider's overall score. We have always received it and
               never shown it, so the officer was deciding from the rule list
               alone -- the provider's own team says the score matters more.
               Shown with its caveat: it is their scale, not a percentage, and
               they supply no maximum for the transaction-level figure. An
               absent score is itself worth seeing, so this says so rather
               than disappearing. */
            <Alert severity={result.checksFlagged.length > 0 ? 'warning' : 'info'}>
              <Typography variant="body2">
                <strong>
                  Provider risk score: {result.riskScore ?? 'not supplied'}
                </strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {result.riskScore === undefined || result.riskScore === null
                  ? 'The provider returned no score for this transaction. Decide from the flagged rules and your own checks.'
                  : 'Platforma365’s own scale, not a percentage, and no maximum is supplied for the overall figure. Read it together with the flagged rules below.'}
              </Typography>
            </Alert>
          )}
          {result.checksFlagged.length > 0 && (
            <Typography variant="body2">
              Flagged rules: {result.checksFlagged.join(', ')}
            </Typography>
          )}
          {result.findings.map((finding, index) => (
            <Typography variant="body2" key={`${finding.rule}:${index}`}>
              <strong>{finding.rule || 'Finding'}:</strong> {finding.reason} (score: {finding.score ?? 'not supplied'};
              {' maximum: '}{finding.maxScore ?? 'not supplied'})
            </Typography>
          ))}
          {!!result.clearedBy && (
            <Typography variant="body2">
              Cleared by {result.clearedBy} at {new Date(result.clearedAt).toLocaleString(APP_LOCALE)}.{' '}
              {result.clearanceReason}
            </Typography>
          )}
          {result.localRules && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">Norvix rules</Typography>
              {result.localRules.findings.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No enabled local rules apply to this transaction.
                </Typography>
              )}
              {result.localRules.findings.map((finding) => (
                <Alert
                  key={finding.ruleId}
                  severity={finding.outcome === 'CLEAR' ? 'success' : 'warning'}
                >
                  <strong>{finding.ruleName}</strong> — {finding.reason}
                </Alert>
              ))}
            </Stack>
          )}
          {!ready && result.reviewVersion !== '' && (
            <>
              <TextField
                label="Compliance clearance reason"
                multiline
                minRows={2}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                inputProps={{ maxLength: 2000 }}
                disabled={busy}
                helperText="Describe the checks completed, evidence reviewed and why release is permitted. For unavailable screening, document the manual screening performed."
              />
              <Button
                variant="outlined"
                onClick={() => {
                  void clear();
                }}
                disabled={busy || reason.trim().length < 10}
              >
                Record compliance clearance
              </Button>
            </>
          )}
        </>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          disabled={busy}
          onClick={() => {
            void load(result?.screened === false);
          }}
        >
          {busy ? 'Checking…' : result?.screened === false ? 'Retry screening' : 'Reload screening'}
        </Button>
        {result?.screened === true && (
          /* Screening the whole transaction is the only screening the provider
             offers: both parties go in together and one result comes back.
             There is no endpoint that checks a beneficiary name on its own. */
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            onClick={() => {
              void load(true);
            }}
          >
            Screen again
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

function formatEurMinor(value: string): string {
  if (!/^\d+$/.test(value)) return 'unavailable';
  const padded = value.padStart(3, '0');
  return `${padded.slice(0, -2)}.${padded.slice(-2)}`;
}
